import React, { useRef, useEffect, useState } from 'react';
import type { SpinWheelComponent } from '../../../types/builder';

interface SpinWheelRendererProps {
  component: SpinWheelComponent;
}

export const SpinWheelRenderer: React.FC<SpinWheelRendererProps> = ({ component }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);

  const { 
    title, 
    subtitle, 
    prizes, 
    buttonText, 
    wheelSize, 
    backgroundColor, 
    textColor, 
    accentColor,
    padding,
    style 
  } = component.settings;

  // Draw the wheel
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || prizes.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = wheelSize / 2;
    const centerY = wheelSize / 2;
    const radius = wheelSize / 2 - 10;

    // Clear canvas
    ctx.clearRect(0, 0, wheelSize, wheelSize);

    // Save context
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);

    // Draw each prize segment
    const arcSize = (2 * Math.PI) / prizes.length;
    
    prizes.forEach((prize, index) => {
      const angle = arcSize * index;
      
      // Draw segment
      ctx.beginPath();
      ctx.fillStyle = prize.color;
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius, angle, angle + arcSize);
      ctx.lineTo(0, 0);
      ctx.fill();
      
      // Draw border
      ctx.strokeStyle = style === 'neon' ? accentColor : '#ffffff';
      ctx.lineWidth = style === 'neon' ? 3 : 2;
      ctx.stroke();

      // Draw text
      ctx.save();
      ctx.rotate(angle + arcSize / 2);
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      ctx.font = style === 'minimal' ? '12px Arial' : 'bold 14px Arial';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 4;
      ctx.fillText(prize.label, radius * 0.7, 0);
      ctx.restore();
    });

    // Draw center circle
    ctx.beginPath();
    ctx.fillStyle = style === 'neon' ? '#000000' : accentColor;
    ctx.arc(0, 0, 20, 0, 2 * Math.PI);
    ctx.fill();
    
    if (style === 'neon') {
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 3;
      ctx.stroke();
    }

    ctx.restore();

    // Draw pointer (triangle at top)
    ctx.beginPath();
    ctx.fillStyle = accentColor;
    ctx.moveTo(centerX, 5);
    ctx.lineTo(centerX - 15, 30);
    ctx.lineTo(centerX + 15, 30);
    ctx.closePath();
    ctx.fill();
    
    // Pointer border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [prizes, rotation, wheelSize, accentColor, style]);

  // Handle spin (mock for builder preview)
  const handleSpin = () => {
    if (isSpinning || prizes.length === 0) return;
    
    setIsSpinning(true);
    
    // Random rotation (3-5 full rotations + random angle)
    const spins = 3 + Math.random() * 2;
    const randomAngle = Math.random() * 360;
    const totalRotation = rotation + spins * 360 + randomAngle;
    
    // Animate rotation
    const startTime = Date.now();
    const duration = component.settings.spinDuration;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentRotation = rotation + (totalRotation - rotation) * easeOut;
      
      setRotation(currentRotation % 360);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsSpinning(false);
      }
    };
    
    requestAnimationFrame(animate);
  };

  // Style variants
  const getContainerStyle = () => {
    const baseStyle = {
      padding: `${padding}px`,
    };

    if (backgroundColor.startsWith('linear-gradient')) {
      return {
        ...baseStyle,
        backgroundImage: backgroundColor,
        color: textColor,
      };
    }
    return {
      ...baseStyle,
      background: backgroundColor,
      color: textColor,
    };
  };

  const getWheelContainerClass = () => {
    switch (style) {
      case 'modern':
        return 'shadow-2xl rounded-full p-2 bg-white/10 backdrop-blur-sm';
      case 'neon':
        return 'shadow-2xl rounded-full p-2 border-4 border-' + accentColor + ' animate-pulse';
      case 'minimal':
        return 'shadow-lg rounded-full';
      default:
        return 'shadow-xl rounded-full p-1 bg-white/5';
    }
  };

  const getButtonClass = () => {
    const baseClass = 'px-8 py-3 rounded-full font-bold text-lg transition-all duration-300 shadow-lg';
    
    switch (style) {
      case 'modern':
        return `${baseClass} bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white transform hover:scale-105`;
      case 'neon':
        return `${baseClass} bg-black border-4 text-white hover:bg-gray-900 animate-pulse`;
      case 'minimal':
        return `${baseClass} bg-white text-gray-900 hover:bg-gray-100`;
      default:
        return `${baseClass} bg-white text-gray-900 hover:bg-gray-100 transform hover:scale-105`;
    }
  };

  return (
    <div style={getContainerStyle()}>
      <div className="max-w-lg mx-auto text-center space-y-6">
        {/* Title */}
        {title && (
          <h2 className="text-2xl font-bold" style={{ color: textColor }}>
            {title}
          </h2>
        )}

        {/* Subtitle */}
        {subtitle && (
          <p className="text-sm opacity-90" style={{ color: textColor }}>
            {subtitle}
          </p>
        )}

        {/* Wheel */}
        <div className="flex justify-center">
          <div className={getWheelContainerClass()}>
            <canvas
              ref={canvasRef}
              width={wheelSize}
              height={wheelSize}
              className="rounded-full"
            />
          </div>
        </div>

        {/* Spin Button */}
        <button
          onClick={handleSpin}
          disabled={isSpinning}
          className={getButtonClass()}
          style={style === 'neon' ? { borderColor: accentColor, color: accentColor } : {}}
        >
          {isSpinning ? '🌀 Spinning...' : buttonText}
        </button>

        {/* Preview Note */}
        <p className="text-xs opacity-60" style={{ color: textColor }}>
          Preview mode - Click button to test animation
        </p>
      </div>
    </div>
  );
};

